import { type ComponentProps, memo } from 'react';

import { cn } from '@air/utils-tailwind';

import { type CreditUsageDisplayCategory, type CreditUsageStats } from '~/components/CreditUsageMeter/types';
import {
  CREDIT_USAGE_CATEGORIES,
  CREDIT_USAGE_CATEGORY_COLOR_MAP,
  getDisplayCategoryUsage,
  getStorageDisplayCategory,
  shouldRenderDisplayCategory,
} from '~/components/CreditUsageMeter/utils';
import { useSubscriptionContext } from '~/providers/SubscriptionProvider';
import { useHasAnyAiAddons } from '~/swr-hooks/workspaces/addOns/useHasAnyAiAddons';

const LABEL_MAP = {
  generative: 'AI Actions',
  enrichedStorage: 'Enriched storage',
  storage: 'Storage',
} as const satisfies Record<CreditUsageDisplayCategory, string>;

export type CreditUsageMeterLegendProps = Pick<ComponentProps<'div'>, 'className'> &
  Pick<CreditUsageStats, 'used'> & {
    /** When provided and > 0, adds a "One-time gift" entry to the legend. */
    giftCreditsBalance?: number;
  };

export const CreditUsageMeterLegend = memo(
  ({ used, className, giftCreditsBalance = 0 }: CreditUsageMeterLegendProps) => {
    const { data: subscription } = useSubscriptionContext();
    const hasAiAddons = useHasAnyAiAddons();

    return (
      <div className={cn('flex flex-wrap gap-x-2 gap-y-1', className)}>
        {CREDIT_USAGE_CATEGORIES.map((category) => {
          const usage = getDisplayCategoryUsage(used, category);

          if (!shouldRenderDisplayCategory({ category, usage, isCreditModel: !!subscription?.isCreditModel })) {
            return null;
          }

          const variant = category === 'generative' ? category : getStorageDisplayCategory(hasAiAddons);

          return (
            <div className="flex items-center gap-1 ads:text-metadata text-12 text-gray-9" key={category}>
              <span className={cn('size-1.5 shrink-0 rounded-full', CREDIT_USAGE_CATEGORY_COLOR_MAP[variant])} />
              {LABEL_MAP[variant]}
            </div>
          );
        })}

        {giftCreditsBalance > 0 && (
          <div className="flex items-center gap-1 ads:text-metadata text-12 text-gray-9">
            <span className="size-1.5 shrink-0 rounded-full ads:bg-ads-altitude-9 bg-blue-9" />
            One-time gift
          </div>
        )}
      </div>
    );
  },
);

CreditUsageMeterLegend.displayName = 'CreditUsageMeterLegend';
